import React from "react";
import { Card } from "react-bootstrap";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormCheck } from "go-form";
import { ReactComponent as BinSVG } from "go-core/images/svg/actions/rubbish.svg";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { apiOrganization } from "../../../../../../../../../services/Api/Organization/apiOrganization";
import { ApplicationApi } from "../../../../../../../../../services/Api/Organization/types";
import { SearchSelectApi } from "../../../../../../../../../services/Api/types";

interface Props {
	index: number;
	form: UseFormReturn<ApplicationApi>;
	updatedConfig: Record<string, any> | undefined;
	setUpdatedConfig: (config: Record<string, any> | undefined) => void;
}

const GoOrderMenuForm = ({ index, form, updatedConfig, setUpdatedConfig }: Props) => {
	const { t } = useTranslation();
	const {
		formState: { errors },
		control,
		register,
		watch,
		setValue,
	} = form;
	const orderTypes = ["DINE_IN", "PICK_UP", "ROOM_SERVICE", "DELIVERY"];
	const orderTypeOptions = orderTypes.map((type) => ({ id: type, label: t(`enums.orders.types.${type}`) }));
	const watchedMenus: Record<string, any>[] = watch("settings.menu_infos");

	const searchMenu = (search: string, params: Record<string, any>, options?: Record<string, any>) => {
		return apiOrganization.getMenusSearchSelect(
			search,
			{
				...params,
				"type|e": "LIST",
			},
			{
				cancelToken: options?.token,
			}
		);
	};

	const searchAvailability = (search: string, params: Record<string, any>, options?: Record<string, any>) => {
		return apiOrganization.getAvailabilitiesSearchSelect(search, params, {
			cancelToken: options?.token,
		});
	};

	const handleRemoveMenu = () => {
		if (index === 0 && watchedMenus.length === 1) {
			setValue("settings.menu_infos", [
				{
					menu_id: undefined,
					availability_id: undefined,
					order_types: [],
				},
			]);
			const config = { ...updatedConfig };
			config.menus = [];
			config.availabilities = [];
			setUpdatedConfig(config);
		} else {
			const newWatchedMenus = watchedMenus.filter((_, menuIndex) => menuIndex !== index);
			setValue("settings.menu_infos", newWatchedMenus);

			const config = { ...updatedConfig };
			const newConfigMenus = (config.menus || []).filter(
				(_: Record<string, any>, configMenuIndex: number) => configMenuIndex !== index
			);
			const newConfigAvailabilities = (config.availabilities || []).filter(
				(_: Record<string, any>, configAvailabilityIndex: number) => configAvailabilityIndex !== index
			);
			config.menus = newConfigMenus;
			config.availabilities = newConfigAvailabilities;
			setUpdatedConfig(config);
		}
	};

	const handleUpdateConfig = (
		value: SearchSelectApi | undefined,
		field: "menu" | "availability",
		menuIndex: number
	) => {
		const config = { ...updatedConfig };
		const newMenus: { id: string; name: string; availability: { id: string; name: string } | undefined }[] = [
			...(config.menus || []),
		];

		if (newMenus[menuIndex]) {
			if (field === "menu") {
				newMenus[menuIndex].id = value ? value.id.toString() : "";
				newMenus[menuIndex].name = value ? value.label : "";
			}
			if (field === "availability") {
				newMenus[menuIndex] = {
					...newMenus[menuIndex],
					availability: value ? { id: value.id.toString(), name: value.label } : undefined,
				};
			}
		} else {
			newMenus.push({
				id: value ? value.id.toString() : "",
				name: value ? value.label : "",
				availability: value ? { id: value.id.toString(), name: value.label } : undefined,
			});
		}
		config.menus = newMenus;

		setUpdatedConfig(config);
	};

	const isRemoveIconVisible = () => {
		if (
			index === 0 &&
			watchedMenus.length === 1 &&
			(watch(`settings.menu_infos.${index}.menu_id`) ||
				watch(`settings.menu_infos.${index}.availability_id`) ||
				watch(`settings.menu_infos.${index}.order_types`)?.length > 0)
		)
			return true;

		return watchedMenus.length > 1;
	};

	return (
		<Card className="go-order-menu-card">
			<Card.Body className="p-3">
				<div className="row">
					<div className="col-6">
						<FormSelectGroup
							name={`settings.menu_infos.${index}.menu_id`}
							getOptionValue={(option) => option.id}
							label={t("common.word.menu")}
							errors={errors}
							loadOptions={searchMenu}
							control={control}
							onChange={(_, fullObj) => handleUpdateConfig(fullObj, "menu", index)}
							data-testid={`settings.menu_infos.${index}.menu_id`}
							defaultValue={{
								label: updatedConfig?.menus[index]?.name,
								id: watch(`settings.menu_infos.${index}.menu_id`),
							}}
						/>
					</div>
					<div className="col-6">
						<FormSelectGroup
							name={`settings.menu_infos.${index}.availability_id`}
							getOptionValue={(option) => option.id}
							label={t("common.word.availability")}
							errors={errors}
							defaultValue={{
								label: updatedConfig?.menus[index]?.availability?.name,
								id: watch(`settings.menu_infos.${index}.availability_id`),
							}}
							loadOptions={searchAvailability}
							onChange={(_, fullObj) => handleUpdateConfig(fullObj, "availability", index)}
							control={control}
							data-testid={`settings.menu_infos.${index}.availability_id`}
						/>
					</div>
				</div>
				<FormSelectGroup
					name={`settings.menu_infos.${index}.order_types`}
					getOptionValue={(option) => option.id}
					label={t("modules.app.field.order_types.title")}
					errors={errors}
					control={control}
					options={orderTypeOptions}
					data-testid={`settings.menu_infos.${index}.order_types`}
					isMulti
				/>
				<div className="d-flex align-items-center justify-content-between">
					<FormCheck
						type="switch"
						register={register}
						label={t("modules.app.field.pick_date.title")}
						name={`settings.menu_infos.${index}.pick_date`}
						errors={errors}
					/>
					{isRemoveIconVisible() && <BinSVG className="icon" onClick={handleRemoveMenu} />}
				</div>
			</Card.Body>
		</Card>
	);
};

export default GoOrderMenuForm;
